import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { PanelFieldBox, notSet } from '@tailwindComponents/PanelFieldBox';
import { SkuActionsMenu, SkuMenuOption } from './SkuActionsMenu';
import {
  DimensionsEditor,
  PanelControlRow,
  PanelInput,
  PanelSelect,
  formatDimensions,
} from './panelControls';
import {
  PALLET_DIMENSION_UNIT_OPTIONS,
  PALLET_TYPE_OPTIONS,
  PALLET_WEIGHT_UNIT_OPTIONS,
  PalletPanelDraft,
  draftPalletVolume,
  draftSkusPerPallet,
} from './panelEdit';

export interface PalletDetailPanelProps {
  position: number;
  draft: PalletPanelDraft;
  onDraftChange: (draft: PalletPanelDraft) => void;
  isEditing: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  menuId: string;
  menuOptions: SkuMenuOption[];
}

export function PalletDetailPanel({
  position,
  draft,
  onDraftChange,
  isEditing,
  isExpanded,
  onToggleExpand,
  menuId,
  menuOptions,
}: PalletDetailPanelProps) {
  const [isFurtherInformationOpen, setIsFurtherInformationOpen] =
    useState(false);
  const patch = (change: Partial<PalletPanelDraft>) =>
    onDraftChange({ ...draft, ...change });
  const dimensionsText = formatDimensions(draft.dimensions);

  return (
    <div className='relative rounded-md border border-gray-300 bg-gray-50/50'>
      <button
        type='button'
        aria-expanded={isExpanded}
        className='flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm font-semibold text-gray-900'
        onClick={onToggleExpand}
      >
        <span>Pallet {position} details</span>
        <span className='truncate text-xs font-normal text-gray-500'>
          {draft.type}
        </span>
        <ChevronDownIcon
          className={`ml-auto h-4 w-4 text-gray-500 transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isExpanded && (
        <div className='px-3.5 pb-3.5'>
          <div className='absolute right-3 top-2.5'>
            <SkuActionsMenu id={menuId} options={menuOptions} />
          </div>

          <div className='flex flex-wrap gap-3'>
            <PanelFieldBox
              isEmphasised
              className='min-w-[190px] flex-1'
              label='Pallet type'
              isEditing={isEditing}
              value={draft.type || notSet}
              editor={
                <PanelSelect
                  ariaLabel='Pallet type'
                  value={draft.type}
                  options={PALLET_TYPE_OPTIONS}
                  onChange={(type) => patch({ type })}
                />
              }
            />
            <PanelFieldBox
              isEmphasised
              className='min-w-[330px] flex-[2]'
              label='Dimensions (L × W × H)'
              isEditing={isEditing}
              value={dimensionsText ?? notSet}
              editor={
                <DimensionsEditor
                  label='Pallet dimensions'
                  unitOptions={PALLET_DIMENSION_UNIT_OPTIONS}
                  value={draft.dimensions}
                  onChange={(dimensions) => patch({ dimensions })}
                />
              }
            />
            <PanelFieldBox
              isEmphasised
              className='min-w-[190px] flex-1'
              label='SKUs per pallet'
              calculatedNote='(calculated)'
              isEditing={false}
              value={draftSkusPerPallet(draft)}
              editor={null}
            />
            <PanelFieldBox
              isEmphasised
              className='min-w-[190px] flex-1'
              label='Gross weight'
              isEditing={isEditing}
              value={
                draft.weight
                  ? `${draft.weight} ${draft.weightUnitOfMeasure}`
                  : notSet
              }
              editor={
                <PanelControlRow>
                  <PanelInput
                    isCompact
                    ariaLabel='Pallet gross weight'
                    value={draft.weight}
                    onChange={(weight) => patch({ weight })}
                  />
                  <PanelSelect
                    isAuto
                    ariaLabel='Pallet gross weight unit'
                    value={draft.weightUnitOfMeasure}
                    options={PALLET_WEIGHT_UNIT_OPTIONS}
                    onChange={(weightUnitOfMeasure) =>
                      patch({ weightUnitOfMeasure })
                    }
                  />
                </PanelControlRow>
              }
            />
            <PanelFieldBox
              isEmphasised
              className='min-w-[190px] flex-1'
              label='Volume'
              calculatedNote='(calculated)'
              isEditing={false}
              value={draftPalletVolume(draft)}
              editor={null}
            />
          </div>

          <div className='mt-3.5'>
            <button
              type='button'
              aria-expanded={isFurtherInformationOpen}
              className='inline-flex items-center gap-2 text-sm font-semibold text-gray-700'
              onClick={() => setIsFurtherInformationOpen((isOpen) => !isOpen)}
            >
              Further information
              <ChevronDownIcon
                className={`h-4 w-4 text-gray-500 transition-transform ${
                  isFurtherInformationOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {isFurtherInformationOpen && (
              <div className='mt-2.5 grid grid-cols-2 gap-3'>
                <PanelFieldBox
                  label='SKUs per layer'
                  isEditing={isEditing}
                  value={draft.skusPerLayer || notSet}
                  editor={
                    <PanelInput
                      ariaLabel='SKUs per layer'
                      value={draft.skusPerLayer}
                      onChange={(skusPerLayer) => patch({ skusPerLayer })}
                    />
                  }
                />
                <PanelFieldBox
                  label='# of Layers'
                  isEditing={isEditing}
                  value={draft.layers || notSet}
                  editor={
                    <PanelInput
                      ariaLabel='Number of layers'
                      value={draft.layers}
                      onChange={(layers) => patch({ layers })}
                    />
                  }
                />
                <PanelFieldBox
                  isOptional
                  className='col-span-2'
                  label='Pallet specification'
                  isEditing={isEditing}
                  value={draft.customPalletSpecification || notSet}
                  editor={
                    <PanelInput
                      ariaLabel='Pallet specification'
                      value={draft.customPalletSpecification}
                      onChange={(customPalletSpecification) =>
                        patch({ customPalletSpecification })
                      }
                    />
                  }
                />
              </div>
            )}
          </div>

          {draft.isNew && (
            <p className='mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700'>
              New pallet: enter SKUs per layer and the number of layers before
              saving.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
